import React, { useState, useEffect } from 'react';
import { Card, Table, Tag, Button, Space, Modal, Descriptions, Input, Select, DatePicker, message, Row, Col, Statistic } from 'antd';
import { SearchOutlined, EyeOutlined, ReloadOutlined } from '@ant-design/icons';
import { get } from '../../utils/merchantApi';
import dayjs from 'dayjs';

const { RangePicker } = DatePicker;
const { Option } = Select;

const MerchantOrders = () => {
  const [loading, setLoading] = useState(false);
  const [orders, setOrders] = useState([]);
  const [total, setTotal] = useState(0);
  const [currencyStats, setCurrencyStats] = useState([]);
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [detailVisible, setDetailVisible] = useState(false);
  const [orderDetail, setOrderDetail] = useState(null);
  const [filters, setFilters] = useState({
    status: undefined,
    payment_method: undefined,
    keyword: '',
    dateRange: null
  });

  useEffect(() => {
    fetchOrders();
  }, [page]);

  const fetchOrders = async () => {
    setLoading(true);
    try {
      const params = { page, limit };
      if (filters.status) params.status = filters.status;
      if (filters.payment_method) params.payment_method = filters.payment_method;
      if (filters.keyword) params.keyword = filters.keyword;
      if (filters.dateRange?.[0]) params.startDate = filters.dateRange[0].format('YYYY-MM-DD');
      if (filters.dateRange?.[1]) params.endDate = filters.dateRange[1].format('YYYY-MM-DD');

      const response = await get('/merchant/orders', params);
      setOrders(response.data?.list || []);
      setTotal(response.data.pagination?.total || 0);
      setCurrencyStats(response.data.pagination?.currency_stats || []);
    } catch (error) {
      console.error('获取订单失败:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    setPage(1);
    fetchOrders();
  };

  const handleViewDetail = async (record) => {
    try {
      const response = await get(`/merchant/orders/${record.trade_no}`);
      setOrderDetail(response.data);
      setDetailVisible(true);
    } catch (error) {
      message.error('获取订单详情失败');
    }
  };

  const getStatusTag = (status) => {
    const statusMap = {
      pending: { color: 'orange', text: '待支付' },
      paid: { color: 'green', text: '已支付' },
      failed: { color: 'red', text: '失败' },
      expired: { color: 'default', text: '已过期' },
      closed: { color: 'default', text: '已关闭' },
      processing: { color: 'blue', text: '处理中' }
    };
    const config = statusMap[status] || { color: 'default', text: status };
    return <Tag color={config.color}>{config.text}</Tag>;
  };

  const columns = [
    {
      title: '平台订单号',
      dataIndex: 'trade_no',
      key: 'trade_no',
      width: 180
    },
    {
      title: '商户订单号',
      dataIndex: 'out_trade_no',
      key: 'out_trade_no',
      width: 180
    },
    {
      title: '金额',
      dataIndex: 'total_amount',
      key: 'total_amount',
      width: 120,
      render: (val, record) => `${record.currency_code || 'CNY'} ${Number(val || 0).toFixed(2)}`
    },
    {
      title: '支付方式',
      dataIndex: 'payment_method',
      key: 'payment_method',
      width: 100,
      render: (val) => {
        const map = {
          bank_transfer: '银行转账',
          alipay: '支付宝',
          wechat: '微信支付',
          usdt: 'USDT',
          'm-pesa': 'M-Pesa',
          mobile_money: 'Mobile Money'
        };
        return map[val] || val;
      }
    },
    {
      title: '付款人',
      dataIndex: 'payer_info',
      key: 'payer_info',
      width: 150,
      render: (info) => {
        if (!info) return '-';
        if (typeof info === 'string') {
          try { info = JSON.parse(info); } catch (e) { return info; }
        }
        const keys = Object.keys(info);
        if (keys.length === 0) return '-';
        return keys.slice(0, 2).map(k => `${k}: ${info[k]}`).join(', ') + (keys.length > 2 ? '...' : '');
      }
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: getStatusTag
    },
    {
      title: '创建时间',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 160,
      render: (val) => val ? dayjs(val).format('YYYY-MM-DD HH:mm:ss') : '-'
    },
    {
      title: '操作',
      key: 'action',
      width: 100,
      render: (_, record) => (
        <Button type="link" size="small" icon={<EyeOutlined />} onClick={() => handleViewDetail(record)}>
          详情
        </Button>
      )
    }
  ];

  return (
    <div>
      <Card style={{ marginBottom: 16 }}>
        <Row gutter={16}>
          <Col>
            <Statistic title="订单总笔数" value={total} />
          </Col>
          {currencyStats.map((stat, idx) => (
            <Col key={idx}>
              <Statistic 
                title={`订单金额 (${stat.currency_code})`} 
                value={Number(stat.order_amount || 0)} 
                precision={2} 
              />
            </Col>
          ))}
        </Row>
      </Card>
      <Card style={{ marginBottom: 16 }}>
        <Space wrap>
          <Input.Search
            placeholder="搜索订单号"
            allowClear
            style={{ width: 200 }}
            onSearch={(val) => { setFilters({ ...filters, keyword: val }); handleSearch(); }}
          />
          <Select
            placeholder="订单状态"
            allowClear
            style={{ width: 120 }}
            value={filters.status}
            onChange={(val) => setFilters({ ...filters, status: val })}
          >
            <Option value="pending">待支付</Option>
            <Option value="paid">已支付</Option>
            <Option value="failed">失败</Option>
            <Option value="expired">已过期</Option>
          </Select>
          <Select
            placeholder="支付方式"
            allowClear
            style={{ width: 120 }}
            value={filters.payment_method}
            onChange={(val) => setFilters({ ...filters, payment_method: val })}
          >
            <Option value="bank_transfer">银行转账</Option>
            <Option value="alipay">支付宝</Option>
            <Option value="wechat">微信支付</Option>
            <Option value="usdt">USDT</Option>
            <Option value="m-pesa">M-Pesa</Option>
            <Option value="mobile_money">Mobile Money</Option>
          </Select>
          <RangePicker 
            value={filters.dateRange}
            onChange={(dates) => setFilters({ ...filters, dateRange: dates })}
          />
          <Button type="primary" icon={<SearchOutlined />} onClick={handleSearch}>
            搜索
          </Button>
        </Space>
      </Card>

      <Card>
        <Table
          columns={columns}
          dataSource={orders}
          rowKey="id"
          loading={loading}
          pagination={{
            current: page,
            pageSize: limit,
            total: total,
            onChange: setPage,
            showSizeChanger: false
          }}
        />
      </Card>

      <Modal
        title="订单详情"
        open={detailVisible}
        onCancel={() => setDetailVisible(false)}
        footer={null}
        width={700}
      >
        {orderDetail && (
          <Descriptions bordered column={2} size="small">
            <Descriptions.Item label="平台订单号" span={2}>{orderDetail.trade_no}</Descriptions.Item>
            <Descriptions.Item label="商户订单号" span={2}>{orderDetail.out_trade_no}</Descriptions.Item>
            <Descriptions.Item label="金额">{orderDetail.currency_code} {orderDetail.total_amount}</Descriptions.Item>
            <Descriptions.Item label="实际支付金额">{orderDetail.actual_amount || '-'}</Descriptions.Item>
            <Descriptions.Item label="支付方式">{orderDetail.payment_method}</Descriptions.Item>
            <Descriptions.Item label="状态">{getStatusTag(orderDetail.status)}</Descriptions.Item>
            <Descriptions.Item label="商品标题" span={2}>{orderDetail.subject}</Descriptions.Item>
            <Descriptions.Item label="创建时间">{orderDetail.created_at ? dayjs(orderDetail.created_at).format('YYYY-MM-DD HH:mm:ss') : '-'}</Descriptions.Item>
            <Descriptions.Item label="支付时间">{orderDetail.payment_time ? dayjs(orderDetail.payment_time).format('YYYY-MM-DD HH:mm:ss') : '-'}</Descriptions.Item>
            <Descriptions.Item label="过期时间">{orderDetail.expire_time ? dayjs(orderDetail.expire_time).format('YYYY-MM-DD HH:mm:ss') : '-'}</Descriptions.Item>
            <Descriptions.Item label="回调地址" span={2}>{orderDetail.callback_url || '-'}</Descriptions.Item>
            <Descriptions.Item label="第三方交易号" span={2}>{orderDetail.transaction_id || '-'}</Descriptions.Item>
            {orderDetail.payer_info && (
              <Descriptions.Item label="付款人信息" span={2}>
                {typeof orderDetail.payer_info === 'object' 
                  ? Object.entries(orderDetail.payer_info).map(([key, val]) => (
                      <div key={key}><span style={{ color: '#666' }}>{key}:</span> {val}</div>
                    ))
                  : orderDetail.payer_info}
              </Descriptions.Item>
            )}
          </Descriptions>
        )}
      </Modal>
    </div>
  );
};

export default MerchantOrders;
